import { useEffect, useState } from "react";
import { CalendarRange, ChevronDown, ChevronUp } from "lucide-react";
import { gameRequest } from "./gameApi";
import { GameBadge } from "./GameBadge";

const weeksWord = (n) => `${n} ${n === 1 ? "week" : "weken"}`;

// Round O — finished quarters (week = quarter of its Thursday), newest first. Fail-soft: hidden on error.
export const PastQuarters = ({ prefix = "game-past-quarters" }) => {
  const [quarters, setQuarters] = useState([]);
  const [openKey, setOpenKey] = useState(null);
  useEffect(() => {
    gameRequest("/game/quarters").then((d) => setQuarters(d.quarters || [])).catch(() => setQuarters([]));
  }, []);
  if (!quarters.length) return null;
  return (
    <section className="game-standing mt-6" data-testid={prefix}>
      <h2 className="font-display text-base md:text-lg font-bold flex items-center gap-2 mb-3">
        <CalendarRange size={17} className="text-tw-yellow" />Vorige kwartalen
      </h2>
      <ul className="grid gap-[3px]">
        {quarters.map((q) => {
          const key = `${q.year}-${q.quarter}`;
          return <PastQuarterRow key={key} q={q} open={openKey === key} prefix={`${prefix}-${key}`}
            onToggle={() => setOpenKey(openKey === key ? null : key)} />;
        })}
      </ul>
    </section>
  );
};

const PastQuarterRow = ({ q, open, onToggle, prefix }) => {
  const nobody = !q.winners?.length || !q.weeks_won;
  return (
    <li className="border border-tw bg-tw-black/40" data-testid={prefix}>
      <button type="button" onClick={onToggle} aria-expanded={open} data-testid={`${prefix}-toggle`}
        className="tw-press w-full flex flex-wrap md:flex-nowrap items-center gap-x-3 gap-y-1 px-3 py-3 text-left text-sm hover:bg-white/5 transition-colors duration-150">
        <span className="min-w-[9rem] flex-1 break-words">
          <strong className="text-white" data-testid={`${prefix}-label`}>{q.label}</strong>
          <span className="block font-mono text-[10px] text-tw-muted mt-0.5">{weeksWord(q.weeks.length)} gespeeld</span>
        </span>
        {open ? <ChevronUp size={14} className="text-tw-muted shrink-0 md:order-last" /> : <ChevronDown size={14} className="text-tw-muted shrink-0 md:order-last" />}
        {nobody ? (
          <span className="text-tw-muted text-xs basis-full md:basis-auto" data-testid={`${prefix}-nobody`}>Niemand won een week</span>
        ) : (
          <span className="flex items-center gap-1 basis-full md:basis-auto min-w-0 md:justify-end" data-testid={`${prefix}-winners`}>
            <GameBadge crown label="Winnaar" testId={`${prefix}-crown`} />
            <span className="min-w-0 break-words">{q.winners.join(", ")}</span>
            <strong className="font-mono whitespace-nowrap ml-1">{weeksWord(q.weeks_won)}</strong>
          </span>
        )}
      </button>
      {open && (
        <div className="border-t border-tw" data-testid={`${prefix}-detail`}>
          <ol className="game-ranking" data-testid={`${prefix}-ranking`}>
            {q.standings.map((row) => (
              <li key={row.user_id} className={row.is_me ? "is-me" : ""} data-testid={`${prefix}-row-${row.user_id}`}>
                <span className="game-position">{nobody ? "–" : row.position}</span>
                <span className="min-w-0 break-words">{row.name}{row.is_me && <span className="text-tw-yellow text-xs ml-2">jij</span>}</span>
                <strong className="game-score">{row.weeks_won}<span className="text-tw-muted text-xs font-normal ml-1">gew.</span></strong>
              </li>
            ))}
          </ol>
          <ul className="px-3 py-3 grid gap-1 border-t border-tw text-xs" data-testid={`${prefix}-weeks`}>
            {q.weeks.map((w, i) => (
              <li key={`${w.iso_year}-${w.iso_week}-${i}`} className="flex flex-wrap gap-x-2 break-words" data-testid={`${prefix}-week-${w.iso_year}-${w.iso_week}`}>
                <strong className="text-white">Week {w.iso_week}</strong>
                {(w.focus_category || w.season_name) && <span className="text-tw-muted">· {w.focus_category || w.season_name}</span>}
                <span className="text-tw-muted">· {w.winners.length ? w.winners.join(", ") : "niemand"}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  );
};
